"use client";

import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import styles from "./SignUp.module.css";
import { FaEnvelope, FaLock, FaExclamationCircle, FaUser, FaMapMarkerAlt, FaCalendarAlt, FaGraduationCap, FaUpload } from "react-icons/fa";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import GuestNav from './Navbars/GuestNav';
import Footer from './Footer';
import countryList from "react-select-country-list";

// Import images
import pencil from "../assets/pencil.png";
import cross from "../assets/cross.png";
import login1 from "../assets/login1.png";
import login2 from "../assets/login2.png";

const SignUp = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    parentName: "",
    email: "",
    password: "",
    confirmPassword: "",
    gender: null,
    location: "",
    country: null,
    dateOfBirth: null,
    schoolCode: "",
  });
  const [errors, setErrors] = useState({});
  const [avatar, setAvatar] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData({
      ...formData,
      [name]: selectedOption,
    });
  };

  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      dateOfBirth: date,
    });
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatar(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setAvatar(null);
      setAvatarPreview(null);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = "First Name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last Name is required";
    if (!formData.username.trim()) newErrors.username = "Username is required";
    if (!formData.parentName.trim()) newErrors.parentName = "Parent Name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email address is invalid";
    }
    if (!formData.password) newErrors.password = "Password is required";
    else if (formData.password.length < 6) newErrors.password = "Password must be at least 6 characters";
    if (!formData.confirmPassword) newErrors.confirmPassword = "Confirm Password is required";
    else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = "Passwords do not match";
    if (!formData.gender) newErrors.gender = "Gender is required";
    if (!formData.location.trim()) newErrors.location = "Address is required";
    if (!formData.country) newErrors.country = "Country is required";
    if (!formData.dateOfBirth) newErrors.dateOfBirth = "Date of Birth is required";
    if (!formData.schoolCode.trim()) newErrors.schoolCode = "School Code is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fill in all required fields correctly!", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    setLoading(true);
    try {
      const data = new FormData();
      for (const key in formData) {
        if (formData[key] !== null && formData[key] !== undefined) {
          if (key === "gender" || key === "country") {
            data.append(key, formData[key].value);
          } else if (key === "dateOfBirth") {
            data.append(key, formData[key].toISOString());
          } else {
            data.append(key, formData[key]);
          }
        }
      }
      if (avatar) {
        data.append("avatar", avatar);
      }

      const response = await fetch(`${process.env.REACT_APP_LOCAL_SERVER_API}/signup`, {
        method: "POST",
        body: data,
      });

      if (response.ok) {
        toast.success("Signup successful! Please log in.", {
          position: "top-center",
          autoClose: 2000,
        });
        navigate("/sign-in");
      } else {
        const errorData = await response.json();
        console.error("Signup failed:", errorData);
        toast.error(errorData.message || "Signup failed!", {
          position: "top-center",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error during signup:", error);
      toast.error("An error occurred during signup. Please try again.", {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Gender options
  const genderOptions = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "other", label: "Other" },
  ];

  // Country options using react-select-country-list
  const countryOptions = useMemo(() => countryList().getData(), []);

  return (
    <>
      <GuestNav />
      <div className={styles.signupContainer}>
        <ToastContainer position="top-center" autoClose={3000} pauseOnFocusLoss={false} draggable pauseOnHover={false} />
        <div className={styles.signupBox}>
          <img src={login1} alt="Login decoration 1" className={styles.login1} />
          <img src={login2} alt="Login decoration 2" className={styles.login2} />
          <img src={cross} alt="Close" className={styles.crossIcon} onClick={() => navigate("/sign-in")} />

          <div className={styles.headerSection}>
            <img src={pencil} alt="Pencil" className={styles.pencilIcon} />
            <h2 className={styles.signupHeading}>Signup to Polymath Kids</h2>
          </div>

          <div className={styles.avatarUploadContainer}>
            <label htmlFor="avatar-upload" className={styles.avatarCircle}>
              {avatarPreview ? (
                <img src={avatarPreview} alt="Avatar Preview" className={styles.avatarPreview} />
              ) : (
                <FaUpload className={styles.uploadIcon} />
              )}
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                style={{ display: "none" }}
              />
            </label>
            <span className={styles.uploadText}>Upload Avatar</span>
          </div>

          <form onSubmit={handleSubmit} className={styles.signupForm}>
            <div className={styles.formGrid}>
              <div className={styles.columnLeftColumn}>
                <div className={styles.formGroupFirstNameField}>
                  <div className={styles.inputWrapper}>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      placeholder="First Name"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={errors.firstName ? `${styles.error}` : ""}
                    />
                    <FaUser className={styles.inputIcon} />
                  </div>
                  {errors.firstName && (
                    <span className={styles.errorMessage}>
                      <FaExclamationCircle /> {errors.firstName}
                    </span>
                  )}
                </div>

                <div className={styles.formGroupUsernameField}>
                  <div className={styles.inputWrapper}>
                    <input
                      type="text"
                      id="username"
                      name="username"
                      placeholder="Username"
                      value={formData.username}
                      onChange={handleChange}
                      className={errors.username ? `${styles.error}` : ""}
                    />
                    <FaUser className={styles.inputIcon} />
                  </div>
                  {errors.username && (
                    <span className={styles.errorMessage}>
                      <FaExclamationCircle /> {errors.username}
                    </span>
                  )}
                </div>

                <div className={styles.formGroupPasswordField}>
                  <div className={styles.inputWrapper}>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      placeholder="Password"
                      value={formData.password}
                      onChange={handleChange}
                      className={errors.password ? `${styles.error}` : ""}
                    />
                    <FaLock className={styles.inputIcon} />
                  </div>
                  {errors.password && (
                    <span className={styles.errorMessage}>
                      <FaExclamationCircle /> {errors.password}
                    </span>
                  )}
                </div>

                <div className={styles.formGroupGenderField}>
                  <div className={styles.inputWrapper}>
                    <Select
                      id="gender"
                      name="gender"
                      placeholder="Select Gender"
                      value={formData.gender}
                      onChange={(selectedOption) => handleSelectChange("gender", selectedOption)}
                      options={genderOptions}
                      classNamePrefix="react-select"
                      className={errors.gender ? `${styles.errorSelect}` : ""}
                      styles={{
                        control: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          border: 'none !important',
                          boxShadow: 'none !important',
                          minHeight: '58px',
                          borderRadius: '18px',
                          padding: '0 17px',
                          '&:hover': {
                            borderColor: '#DCDCDC'
                          }
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          padding: '0 !important',
                        }),
                        singleValue: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          color: '#333 !important',
                        }),
                        placeholder: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          color: '#888 !important',
                        }),
                        input: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          color: '#333 !important',
                          padding: '0 !important',
                        }),
                        indicatorSeparator: (base) => ({
                          ...base,
                          display: 'none !important',
                        }),
                        dropdownIndicator: (base) => ({
                          ...base,
                          color: '#888 !important',
                        }),
                        menu: (base) => ({
                          ...base,
                          borderRadius: '8px',
                          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                          zIndex: '100',
                        }),
                        option: (base, state) => ({
                          ...base,
                          padding: '10px 17px',
                          fontSize: '16px', // Corrected from 'font-size' to 'fontSize'
                          color: '#333',
                          backgroundColor: state.isSelected ? '#812470' : state.isFocused ? '#e0e0e0' : 'white',
                          '&:active': {
                            backgroundColor: '#812470',
                          },
                        }),
                      }}
                    />
                  </div>
                  {errors.gender && (
                    <span className={styles.errorMessage}>
                      <FaExclamationCircle /> {errors.gender}
                    </span>
                  )}
                </div>

                <div className={styles.formGroupLocationField}>
                  <div className={styles.inputWrapper}>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      placeholder="Address"
                      value={formData.location}
                      onChange={handleChange}
                      className={errors.location ? `${styles.error}` : ""}
                    />
                    <FaMapMarkerAlt className={styles.inputIcon} />
                  </div>
                  {errors.location && (
                    <span className={styles.errorMessage}>
                      <FaExclamationCircle /> {errors.location}
                    </span>
                  )}
                </div>

                <div className={styles.formGroupCountryField}>
                  <div className={styles.inputWrapper}>
                    <Select
                      id="country"
                      name="country"
                      placeholder="Choose your country"
                      value={formData.country}
                      onChange={(selectedOption) => handleSelectChange("country", selectedOption)}
                      options={countryOptions}
                      classNamePrefix="react-select"
                      className={errors.country ? `${styles.errorSelect}` : ""}
                      isSearchable={true}
                      isClearable={true}
                      menuPlacement="auto"
                      styles={{
                        control: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          border: 'none !important',
                          boxShadow: 'none !important',
                          minHeight: '58px',
                          borderRadius: '18px',
                          padding: '0 17px',
                          '&:hover': {
                            borderColor: '#DCDCDC'
                          }
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          padding: '0 !important',
                        }),
                        singleValue: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          color: '#333 !important',
                        }),
                        placeholder: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          color: '#888 !important',
                        }),
                        input: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          color: '#333 !important',
                          padding: '0 !important',
                        }),
                        indicatorSeparator: (base) => ({
                          ...base,
                          display: 'none !important',
                        }),
                        dropdownIndicator: (base) => ({
                          ...base,
                          color: '#888 !important',
                        }),
                        menu: (base) => ({
                          ...base,
                          borderRadius: '8px',
                          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                          zIndex: '100',
                        }),
                        option: (base, state) => ({
                          ...base,
                          padding: '10px 17px',
                          fontSize: '16px', // Corrected from 'font-size' to 'fontSize'
                          color: '#333',
                          backgroundColor: state.isSelected ? '#812470' : state.isFocused ? '#e0e0e0' : 'white',
                          '&:active': {
                            backgroundColor: '#812470',
                          },
                        }),
                      }}
                    />
                  </div>
                  {errors.country && (
                    <span className={styles.errorMessage}>
                      <FaExclamationCircle /> {errors.country}
                    </span>
                  )}
                </div>
              </div>

              <div className={styles.columnRightColumn}>
                <div className={styles.formGroupParentNameField}>
                  <div className={styles.inputWrapper}>
                    <input
                      type="text"
                      id="parentName"
                      name="parentName"
                      placeholder="Parent Name"
                      value={formData.parentName}
                      onChange={handleChange}
                      className={errors.parentName ? `${styles.error}` : ""}
                    />
                    <FaUser className={styles.inputIcon} />
                  </div>
                  {errors.parentName && (
                    <span className={styles.errorMessage}>
                      <FaExclamationCircle /> {errors.parentName}
                    </span>
                  )}
                </div>

                <div className={styles.formGroupLastNameField}>
                  <div className={styles.inputWrapper}>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      placeholder="Last Name"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={errors.lastName ? `${styles.error}` : ""}
                    />
                    <FaUser className={styles.inputIcon} />
                  </div>
                  {errors.lastName && (
                    <span className={styles.errorMessage}>
                      <FaExclamationCircle /> {errors.lastName}
                    </span>
                  )}
                </div>

                <div className={styles.formGroupEmailField}>
                  <div className={styles.inputWrapper}>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="Email"
                      value={formData.email}
                      onChange={handleChange}
                      className={errors.email ? `${styles.error}` : ""}
                    />
                    <FaEnvelope className={styles.inputIcon} />
                  </div>
                  {errors.email && (
                    <span className={styles.errorMessage}>
                      <FaExclamationCircle /> {errors.email}
                    </span>
                  )}
                </div>

                <div className={styles.formGroupConfirmPasswordField}>
                  <div className={styles.inputWrapper}>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      placeholder="Confirm Password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={errors.confirmPassword ? `${styles.error}` : ""}
                    />
                    <FaLock className={styles.inputIcon} />
                  </div>
                  {errors.confirmPassword && (
                    <span className={styles.errorMessage}>
                      <FaExclamationCircle /> {errors.confirmPassword}
                    </span>
                  )}
                </div>

                <div className={styles.formGroupDobField}>
                  <div className={styles.inputWrapper}>
                    <input
                      type="text"
                      id="dateOfBirth"
                      name="dateOfBirth"
                      placeholder="Date of Birth (DD-MM-YYYY)"
                      value={formData.dateOfBirth ? formData.dateOfBirth.toString() : ''}
                      onChange={handleChange}
                      className={errors.dateOfBirth ? `${styles.error}` : ""}
                    />
                  </div>
                  {errors.dateOfBirth && (
                    <span className={styles.errorMessage}>
                      <FaExclamationCircle /> {errors.dateOfBirth}
                    </span>
                  )}
                </div>

                <div className={styles.formGroupSchoolCodeField}>
                  <div className={styles.inputWrapper}>
                    <input
                      type="text"
                      id="schoolCode"
                      name="schoolCode"
                      placeholder="School Code"
                      value={formData.schoolCode}
                      onChange={handleChange}
                      className={errors.schoolCode ? `${styles.error}` : ""}
                    />
                    <FaGraduationCap className={styles.inputIcon} />
                  </div>
                  {errors.schoolCode && (
                    <span className={styles.errorMessage}>
                      <FaExclamationCircle /> {errors.schoolCode}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <button type="submit" className={styles.signupButton} disabled={loading}>
              {loading ? "Signing Up..." : "Sign Up"}
            </button>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default SignUp;